import { useState, useEffect } from "react";
import { FilePdfOutlined } from "@ant-design/icons";
import { AlumnoForm } from "./AlumnoForm";
import InvoiceTicket from "./InvoiceTicket";
import { DownloadDocumentButton, PostInvoiceButton } from "../../common/components";
import db from "../../../../db/database";
import { getInvoiceById } from "../../../../services/invoiceService";
import { syncInvoiceToBC, getFinalDocumentNo } from "../../../../services/invoiceSyncService";
import { getColegioByNo } from "../../../../services/colegioService";
import { getSerieNCFBySeriesCode } from "../../../../services/serieNCFService";
import { downloadInvoicePdfFromBC } from "../../../../utils/businessCentralUtils";
import { ok, fail } from "../../../../utils/common";
import { getVendedorByNo } from "../../../../services/vendorService";
import { COUNTRY_CODES, countryMap } from "../../../../constants/countries"


//************************************InvoiceForm.js slots************************************
import { ClientSectionSlot as ClientSection } from "./ClientSectionSlot";
import { BillingInfoSectionSlot as BillingInfoSection } from "./BillingInfoSectionSlot";
import InvoiceHeaderSlot from "./InvoiceHeader";

export { ClientSection, BillingInfoSection, InvoiceHeaderSlot };

//************************************ InvoiceForm.js methods ************************************
/**
 * Validacion para mostrar modal de confirmación de datos del cliente
 */
export const useConfirmationModal = (props) => {
    const { lines } = props;

    if (!lines) {
        return false;
    }

    const totalUnitPrice = lines.reduce((sum, line) => sum + ((line.Unit_Price || 0) * line.Quantity), 0);

    return totalUnitPrice >= 10000;
};

const getSerieNcfAndNo = async (invoice) => {
    const country = countryMap[COUNTRY_CODES.HONDURAS];
    let No = '';

    try {
        if (!invoice?.No) {
            No = await getFinalDocumentNo(invoice, country);
        }
    }
    catch (error) {
        window.messageApi.destroy(); //Elimino mensaje de error devuelto por getFinalDocumentNo
        return fail(`No se encontró un número de documento final para el número preasignado ${invoice.Pre_Assigned_No}.`);
    }

    const invoiceFinal = await db.invoices.get(invoice.id);

    //este error significa que se contabilizo, pero la api de BC no devuelve las series
    if (!invoiceFinal?.No_Serie_NCF_Facturas && !invoiceFinal?.No_Serie_NCF_Abonos) {
        console.log(`No_Serie_NCF vacío para factura ${No}`)
        return ok(true);
    }

    const NCF = invoiceFinal.Document_Type === 'Credit Memo' ? invoiceFinal.No_Serie_NCF_Abonos : invoiceFinal.No_Serie_NCF_Facturas;

    const serie = await getSerieNCFBySeriesCode(NCF);

    if (!serie) {
        return fail(`Serie ${invoiceFinal.No_Serie_NCF_Facturas} no encontrada`);
    }

    await db.invoices.update(invoice.id, {
        No: No,
        No_Autorizacion_CAI: serie?.No_Autorizacion_CAI,
        Fecha_Autorizacion: serie?.Fecha_Autorizacion,
        Fecha_Caducidad: serie?.Fecha_Caducidad,
        Establecimiento: serie?.Establecimiento,
        Punto_de_Emision: serie?.Punto_de_Emision,
        Tipo_Comprobante: serie?.Tipo_Comprobante,
        Starting_No: serie?.Starting_No,
        Ending_No: serie?.Ending_No,
    });

    return ok(true);
}

export const submitInvoiceAndPrepareFiscalTicket = async ({ invoice, country }) => {
    try {
        await db.invoices.update(invoice.id, {
            syncing: true,
            syncStartTime: new Date().toISOString()
        });

        const result = await syncInvoiceToBC(invoice, country);

        const updatedInvoice = await db.invoices.get(invoice.id);
        const isPosted =
            updatedInvoice?.syncStage === "posted" &&
            updatedInvoice?.posted === 1;

        if (!result.success || !isPosted) {
            return fail(`Error al contabilizar: ${result?.message || "desconocido"}`);
        }

        updatedInvoice.Customer_No = invoice.Customer_No;

        const resultSerieNcfAndNo = await getSerieNcfAndNo(updatedInvoice);

        if (!resultSerieNcfAndNo.ok) {
            return fail(resultSerieNcfAndNo.message);
        }

        return ok(true);
    } catch (error) {
        const updatedInvoice = await db.invoices.get(invoice.id);
        if (updatedInvoice.No) { return ok(true); }
        let message = updatedInvoice
        return fail("No se pudo sincronizar la factura", error);

    } finally {
        await db.invoices.update(invoice.id, {
            syncing: false,
            syncStartTime: null
        });
    }
};



/**
 * COMPONENTE SLOT PARA LA SECCIÓN "CONFIRMACION"
 * Se renderizará en onSubmit de la nueva factura para corroborar datos de facturacion
 */
export const ConfirmationFormSection = ({ handleSubmit, handleCancel, initialData }) => {
    return (
        <div className="confirmation-modal-content">

            <p className="legal-notice">
                Por requisitos legales, es necesario ingresar y confirmar todos los datos del alumno cuando el monto total es igual o superior a L 10.000.
            </p>

            <AlumnoForm isRequired={true} handleSubmit={handleSubmit} handleCancel={handleCancel} initialData={initialData} />
        </div>
    )
}

/**
 * COMPONENTE SLOT PARA LA SECCIÓN "TICKET"
 * Se renderizará al confirmar factura.
 */
export const TicketSection = (props) => {
    let invoice = props.invoice;
    const [enrichedInvoice, setEnrichedInvoice] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchFiscalDetails = async () => {

            if (!invoice) return;

            setIsLoading(true);
            try {
                // si es creacion de factura, obtengo el No final de la factura
                if (!invoice.No) {
                    invoice = await getInvoiceById(invoice.id);
                    //Si sigue siendo vacio - llamo a postedSalesInvoice para verificar si contabilizo
                    try {
                        if (!invoice?.No) {
                            const country = countryMap[COUNTRY_CODES.HONDURAS];
                            const No = await getFinalDocumentNo(invoice, country);
                            invoice = await getInvoiceById(invoice.id);
                        }
                    }
                    catch (error) {
                        window.messageApi.destroy(); //Elimino mensaje de error devuelto por getFinalDocumentNo
                    }
                }

                // obtengo nombre del colegio
                let colegioData = null;
                if (invoice.Colegio) {
                    colegioData = await getColegioByNo(invoice.Colegio);
                }

                // NCF Series data (CAI, Ranges, Expiration) - Solo se llamara por ser de historicos
                let fiscalData = null;
                if (!invoice.Starting_No) {
                    const seriesCode = invoice.Document_Type === 'Credit Memo' ? invoice.No_Serie_NCF_Abonos : invoice.No_Serie_NCF_Facturas;
                    fiscalData = await getSerieNCFBySeriesCode(seriesCode, invoice?.No_Autorizacion_CAI || "");
                }

                // obtengo datos del vendedor
                let vendedorData = null;
                if (invoice.Salesperson_Code) {
                    vendedorData = await getVendedorByNo(invoice.Salesperson_Code);
                }

                setEnrichedInvoice({
                    ...invoice,
                    // solo sobrescribo si se obtuvo fiscalData
                    ...(fiscalData && {
                        No_Autorizacion_CAI: fiscalData.No_Autorizacion_CAI,
                        Starting_No: fiscalData.Starting_No,
                        Ending_No: fiscalData.Ending_No,
                        Fecha_Caducidad: fiscalData.Fecha_Caducidad || fiscalData.Expiration_Date || fiscalData.Expiration_date,
                        Establecimiento: fiscalData.Establecimiento,
                        Punto_de_Emision: fiscalData.Punto_de_Emision,
                        Tipo_Comprobante: fiscalData.Tipo_Comprobante,
                    }),
                    // siempre agrego el colegio si se encuentra
                    colegioNombre: colegioData?.nombre || invoice.colegioNombre || ""
                });
            } catch (error) {
                console.error("Error obteniendo datos fiscales para el ticket", error);
            } finally {
                setIsLoading(false);
            }
        };

        if (props.open) {
            fetchFiscalDetails();
        }
    }, [props.invoice, props.open]);

    if (!props.open) return null;
    if (isLoading) return <div>Cargando datos fiscales...</div>;

    const displayInvoice = enrichedInvoice || invoice;

    return (
        <InvoiceTicket
            {...props}
            invoice={displayInvoice}
        />
    );
};


//************************************InvoiceList.js methods************************************
const handleHondurasInvoiceDownload = async (invoice, setLoading) => {
    try {
        setLoading(true);

        if (!invoice?.systemId) {
            const resultSerieNcfAndNo = await getSerieNcfAndNo(invoice);

            if (!resultSerieNcfAndNo.ok) {
                window.messageApi.error(resultSerieNcfAndNo.message);
                return;
            }

            invoice = await db.invoices.get(invoice.id);

            if (!invoice?.systemId) {
                window.messageApi.error("La factura aún no está contabilizada.");
                return;
            }
        }

        await downloadInvoicePdfFromBC({
            documentNo: invoice.No,
            documentType: invoice.Document_Type,
            fileName: invoice.Document_Type === "Credit Memo"
                ? `NotaCredito_${invoice.No}.pdf`
                : `Factura_${invoice.No}.pdf`,
        });
    }
    catch (error) {
        window.messageApi.error("Error al descargar la factura.");
    }
    finally {
        setLoading(false);
    }
};

//************************************InvoiceList.js slots************************************
export const DownloadTicketButton = (props) => { return null; }

export const DownloadInvoiceButton = (props) => {
    return (
        <DownloadDocumentButton
            {...props}
            icon={<FilePdfOutlined />}
            title="Descargar factura"
            onDownload={handleHondurasInvoiceDownload}
        />
    );
};

export { PostInvoiceButton };